import { useEffect, useState, useCallback } from 'react';
import { Progress, Statistic, Row, Col, Alert, Skeleton, Button } from 'antd';
import { UpCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { storageApi } from '../services/api';
import { formatFileSize } from '../utils/format';
import UpgradeModal from './UpgradeModal';
import OrderHistoryModal from './OrderHistoryModal';
import type { StorageStats as StorageStatsType } from '../types';

interface StorageStatsProps {
  colors: {
    primary: string;
    success: string;
    text: string;
    textMuted: string;
    border: string;
    surface: string;
  };
}

export default function StorageStats({ colors }: StorageStatsProps) {
  const [stats, setStats] = useState<StorageStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [orderHistoryVisible, setOrderHistoryVisible] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageApi.getStats();

      if (response && typeof response === 'object' && 'success' in response && response.success) {
        setStats((response as { data: StorageStatsType }).data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载存储统计失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleUpgradeSuccess = () => {
    setUpgradeModalVisible(false);
    // 刷新存储统计
    fetchStats();
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="无法加载存储统计信息"
          description={error || '未知错误'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const isWarning = stats.usagePercentage >= 70 && stats.usagePercentage < 90;
  const isCritical = stats.usagePercentage >= 90;
  const progressColor = isCritical ? '#EF4444' : isWarning ? '#F59E0B' : colors.primary;

  return (
    <div style={{ padding: '24px' }}>
      {/* 存储空间不足警告 */}
      {stats.cleanupWarning?.warning && stats.cleanupWarning.message && (
        <Alert
          message={stats.cleanupWarning.message}
          type="error"
          showIcon
          style={{
            marginBottom: 24,
            background: '#FEF2F2',
            border: '#EF4444',
          }}
          icon={<span style={{ color: '#EF4444', fontWeight: 'bold' }}>!</span>}
        />
      )}
      <Row gutter={[48, 24]} align="middle">
        {/* Left: Progress Circle */}
        <Col xs={24} sm={8} md={6}>
          <div style={{ textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={stats.usagePercentage}
              strokeColor={progressColor}
              trailColor={colors.border}
              strokeWidth={10}
              width={120}
              format={(percent) => (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: colors.text }}>
                    {percent}%
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>
                    已使用
                  </div>
                </div>
              )}
            />
          </div>
        </Col>

        {/* Right: Stats Grid */}
        <Col xs={24} sm={16} md={18}>
          <Row gutter={[32, 16]}>
            <Col xs={12} sm={8}>
              <Statistic
                title={
                  <span style={{ color: colors.textMuted, fontSize: 14 }}>
                    总空间
                    <span style={{ margin: '0 4px' }}>/</span>
                    <a 
                      onClick={() => setOrderHistoryVisible(true)}
                      style={{
                        color: '#0ca5e8',
                        fontSize: 12,
                        cursor: 'pointer',
                        textDecoration: 'none'
                      }}
                    >
                      <HistoryOutlined style={{ marginRight: 2 }} />
                      付费记录
                    </a>
                  </span>
                }
                value={formatFileSize(stats.totalStorageMb * 1024 * 1024)}
                valueStyle={{
                  color: colors.text,
                  fontSize: 20,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                suffix={
                  <Button
                    type="primary"
                    size="small"
                    icon={<UpCircleOutlined />}
                    onClick={() => setUpgradeModalVisible(true)}
                    style={{
                      background: colors.primary,
                      borderRadius: '4px',
                      fontSize: 12,
                      height: '26px',
                      padding: '0 10px',
                      marginLeft: '4px'
                    }}
                  >
                    付费
                  </Button>
                }
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title={<span style={{ color: colors.textMuted, fontSize: 14 }}>已用空间</span>}
                value={formatFileSize(stats.usedStorageMb * 1024 * 1024)}
                valueStyle={{ 
                  color: progressColor, 
                  fontSize: 20, 
                  fontWeight: 600 
                }}
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title={<span style={{ color: colors.textMuted, fontSize: 14 }}>文件数量</span>}
                value={stats.fileCount}
                suffix="个"
                valueStyle={{ 
                  color: colors.text, 
                  fontSize: 20, 
                  fontWeight: 600 
                }}
              />
            </Col>
          </Row>

          {/* Linear Progress */}
          <div style={{ marginTop: 24 }}>
            <Progress
              percent={stats.usagePercentage}
              strokeColor={progressColor}
              trailColor={colors.border}
              strokeWidth={8}
              showInfo={false}
            />
            <div style={{ 
              marginTop: 8, 
              fontSize: 13, 
              color: colors.textMuted,
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>剩余可用: {formatFileSize(stats.availableStorageMb * 1024 * 1024)}</span>
              {isWarning && <span style={{ color: '#F59E0B' }}>存储空间即将不足</span>}
              {isCritical && <span style={{ color: '#EF4444' }}>存储空间严重不足</span>}
            </div>
          </div>
        </Col>
      </Row>

      {/* 升级弹窗 */}
      <UpgradeModal
        visible={upgradeModalVisible}
        onClose={handleUpgradeSuccess}
        colors={{
          primary: colors.primary,
          text: colors.text,
          textMuted: colors.textMuted,
          border: colors.border,
          surface: colors.surface,
          success: colors.success,
        }}
      />

      {/* 付费记录弹窗 */}
      <OrderHistoryModal
        visible={orderHistoryVisible}
        onClose={() => setOrderHistoryVisible(false)}
        colors={{
          primary: colors.primary,
          text: colors.text,
          textMuted: colors.textMuted,
          border: colors.border,
          surface: colors.surface,
        }}
      />
    </div>
  );
}
